import {
  FormControl,
  FormLabel,
  FormErrorMessage,
} from '@chakra-ui/form-control'

import { useController, FieldValues, UseControllerProps } from 'react-hook-form'

import { Select as ChakraSelect, Props, GroupBase } from 'chakra-react-select'

import { useTranslation } from 'react-i18next'

interface SelectProps<
  FormValues extends FieldValues = FieldValues,
  Option = unknown,
  IsMulti extends boolean = boolean,
  Group extends GroupBase<Option> = GroupBase<Option>
> extends Omit<Props<Option, IsMulti, Group>, 'name' | 'defaultValue'>,
    UseControllerProps<FormValues> {
  label?: string
}

export function ChartSelect<
  FormValues extends FieldValues = FieldValues,
  Option = unknown,
  IsMulti extends boolean = boolean,
  Group extends GroupBase<Option> = GroupBase<Option>
>({
  name,
  label,
  options,
  control,
  rules,
  shouldUnregister,
  ...selectProps
}: SelectProps<FormValues, Option, IsMulti, Group>) {
  const { t } = useTranslation()

  const {
    field,
    fieldState: { error },
  } = useController<FormValues>({
    name,
    control,
    rules,
    shouldUnregister,
  })

  return (
    <FormControl
      isInvalid={!!error}
      display='flex'
      flexDirection='row'
      justifyContent='flex-end'
      alignItems='center'
      zIndex={99}
    >
      {!!label && (
        <FormLabel padding='0' margin='0' htmlFor={name} fontSize="0.875rem" color={'parkoff.orange.300'}>
          {label}
        </FormLabel>
      )}
      <ChakraSelect
        id={name}
        options={options}
        chakraStyles={{
          control: provided => ({
            ...provided,
            borderColor: '#CBCBCB',
            border: 'none !important',
            _focus: {
              border: 'none !important',
            },
          }),
          container: provided => ({
            all: 'unset',
            borderColor: 'none !important',
            _focus: {
              border: 'none !important',
            },
          }),
          dropdownIndicator: provided => ({
            ...provided,
            color: 'parkoff.orange.300',
            width:'fit-content',
            padding: '0 0.5rem',
            border: 'none',
            background: 'transparent',
            _focus: {
              border: 'none !important',
            },
          }),
          placeholder: provided => ({
            all: 'unset',
            color: 'parkoff.orange.300',
            fontSize: '0.875rem',
            _focus: {
              border: 'none !important',
            },
          }),
          valueContainer: provided => ({
            ...provided,
            fontSize: '0.875rem',
            textAlign: 'center',
            width: 'fit-content',
            color: 'parkoff.orange.300',
            padding: '0 0.5rem',
            _hover: {
              cursor: 'pointer',
            },
            _focus: {
              border: 'none !important',
            },
          }),
          option: provided => ({
            ...provided,
            color: 'parkoff.orange.300',
            fontSize: '0.875rem',
            _focus: {
              border: 'none !important',
            },
          }),
          // clearIndicator: provided => ({
          //   all: 'unset',
          // }),
          // downChevron: provided => ({
          //   ...provided,
          //   color: 'parkoff.orange.300',
          //   fontSize: '0.875rem',
          // }),
          // crossIcon: provided => ({
          //   ...provided,
          // }),
          indicatorsContainer: provided => ({
            ...provided,
            border: 'transparent',
          }),
          inputContainer: provided => ({
            ...provided,
            width: 'fit-content',
          }),
          input: provided => ({
            ...provided,
            maxWidth: '4rem',
            width: 'fit-content',
          }),
          singleValue: provided => ({
            ...provided,
            width: 'fit-content',
          }),
          // group?: StylesFunction<GroupProps<Option, IsMulti, Group>>;
          // groupHeading?: StylesFunction<GroupHeadingProps<Option, IsMulti, Group>>;
          // indicatorSeparator?: StylesFunction<IndicatorSeparatorProps<Option, IsMulti, Group>>;
          // loadingIndicator?: StylesFunction<LoadingIndicatorProps<Option, IsMulti, Group>>;
          // loadingMessage?: StylesFunction<NoticeProps<Option, IsMulti, Group>>;
          // menu?: StylesFunction<MenuProps<Option, IsMulti, Group>>;
          // menuList?: StylesFunction<MenuListProps<Option, IsMulti, Group>>;
          // noOptionsMessage?: StylesFunction<NoticeProps<Option, IsMulti, Group>>;
        }}
        {...selectProps}
        {...field}
      />
      {!!error && (
        <FormErrorMessage role="alert">{t(error.message!)}</FormErrorMessage>
      )}
    </FormControl>
  )
}
